import { createMutation, createQuery, request } from '@luna/api';
import type { Language } from '@luna/i18n';
import type { AuthorizationType } from '@shared/core-types';
import { SettingsService } from '@api-client/dashboard';
import type { AppearanceMode } from '@shared/dark-mode-utils';

type SecurityAppVariant = 'APP' | 'DESKTOP' | 'DUAL';

type WebApiUserSettings = {
    language: Language;
    tacPhoneNumbers?: string[];
    authorizationType: AuthorizationType;
    'at-SoftwareTokenInformation'?: {
        variant?: SecurityAppVariant;
    };
    directDebitApprovalsOnboardingDisplayed?: boolean;
};

export type UserSettings = WebApiUserSettings & { appearanceMode: AppearanceMode };

export const [
    useUserSettings,
    {
        queryApi: queryUserSettings,
        updateCache: updateUserSettingsCache,
        selectData: selectUserSettingsData,
    },
] = createQuery<() => Promise<UserSettings>>({
    name: 'user-settings',
    payloadCreator: async () => {
        const dashboardSettings = await SettingsService.getPublicSettings();

        return {
            ...(dashboardSettings._embedded.legacySettings as WebApiUserSettings),
            appearanceMode: dashboardSettings.appearanceMode ?? 'LIGHT',
        };
    },
});

export const [, { mutate: updateWebApiUserSettings }] = createMutation<
    WebApiUserSettings,
    { settingsUrl: string; userSettings: Partial<WebApiUserSettings> }
>({
    name: 'user-settings/update',
    payloadCreator: async ({ settingsUrl, userSettings: userSettingsUpdate }) => {
        const response = await request<WebApiUserSettings>(settingsUrl, {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ ...userSettingsUpdate }),
        });
        const userSettingsData = selectUserSettingsData();
        if (!userSettingsData) {
            return response;
        }

        updateUserSettingsCache({
            data: {
                ...userSettingsData,
                ...userSettingsUpdate,
            },
        });
        return response;
    },
});

export const [, { mutate: useUpdateDashboardUserSettings }] = createMutation({
    name: 'dashboard/public/settings/update-dashboard-user-settings',
    payloadCreator: async (appearanceMode: AppearanceMode): Promise<void> => {
        const response = await SettingsService.patchPublicSettings({
            requestBody: { appearanceMode },
        });
        const userSettingsData = selectUserSettingsData();
        if (!userSettingsData) {
            return response;
        }

        updateUserSettingsCache({
            data: {
                ...userSettingsData,
                appearanceMode,
            },
        });
        return response;
    },
});
